import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { RichText, RichView, isHtmlEmpty } from "@/components/RichText";
import { toast } from "sonner";
import {
  Shield, LogOut, Inbox, Send, Play, Pause, SkipForward, Plus, Minus,
  AlertTriangle, Clock, Lock, NotebookPen, BarChart3, CheckCircle2, XCircle,
  Megaphone, Radio, Truck, Skull, Building, Eye, Settings, ArrowLeft, Hourglass, CalendarClock, ClipboardList, History, FileText,
} from "lucide-react";

const TAG_META = {
  Media: { icon: Megaphone, color: "#FFCC00" },
  Regulator: { icon: Building, color: "#32D74B" },
  Supplier: { icon: Truck, color: "#9BA1A6" },
  "Bad actor": { icon: Skull, color: "#FF3B30" },
  "Internal update": { icon: Radio, color: "#FFFFFF" },
};

export default function Simulation() {
  const { sessionId } = useParams();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [resolvedId, setResolvedId] = useState(sessionId || null);

  useEffect(() => {
    if (!sessionId && user?.role !== "admin") {
      api.get("/sessions").then((r) => {
        const live = r.data.find((s) => s.status === "live" || s.status === "paused") || r.data.find((s) => s.status !== "archived");
        if (live) setResolvedId(live.id);
      });
    }
  }, [sessionId, user]);

  if (!resolvedId) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[var(--bg-base)] text-white">
        <Shield className="w-10 h-10 text-[var(--txt-muted)]" />
        <p className="text-[var(--txt-secondary)]">No active session assigned to you yet.</p>
        <button onClick={() => { logout(); nav("/"); }} className="text-base text-[var(--txt-muted)] hover:text-white">Log out</button>
      </div>
    );
  }
  return <SimRoom sessionId={resolvedId} isAdmin={user?.role === "admin"} user={user} logout={() => { logout(); nav("/"); }} />;
}

function useCountdown(inject, paused) {
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (!inject || !inject.started_at || inject.status !== "active") { setRemaining(0); return; }
    const tick = () => {
      const started = new Date(inject.started_at).getTime();
      const total = (inject.seconds + 30) * 1000;
      setRemaining(Math.max(0, Math.round((started + total - Date.now()) / 1000)));
    };
    tick();
    if (paused) return;
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [inject, paused]);
  return remaining;
}

function fmt(s) {
  const m = Math.floor(s / 60), sec = s % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

function SimRoom({ sessionId, isAdmin, user, logout }) {
  const [data, setData] = useState(null);
  const [session, setSession] = useState(null);
  const [response, setResponse] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [showQ, setShowQ] = useState(false);
  const [openMsg, setOpenMsg] = useState(null);
  const [feedbackView, setFeedbackView] = useState(false);
  const pollRef = useRef();
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data: live } = await api.get(`/sessions/${sessionId}/live`, { skipAuthRedirect: true });
      setData(live);
      if (isAdmin) {
        const { data: s } = await api.get(`/sessions/${sessionId}`);
        setSession(s);
      }
    } catch (e) { /* ignore */ }
  }, [sessionId, isAdmin]);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 3000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  useEffect(() => {
    setResponse("");
  }, [data?.current_inject?.id]); // eslint-disable-line

  const current = data?.current_inject;
  const paused = data?.paused;
  const remaining = useCountdown(current, paused);
  const locked = current?.locked || remaining === 0;
  const lockedOut = data?.locked_until && new Date(data.locked_until) > new Date();
  const ended = data?.session?.status === "ended";

  const submit = async (late = false) => {
    try {
      await api.post(`/sessions/${sessionId}/injects/${current.id}/respond`, { text: response, is_late: late });
      toast.success(late ? "Late addition saved" : "Response submitted");
      setResponse("");
      load();
    } catch (e) {
      if (e.response?.status === 423) toast.error("Responses are locked for this inject");
      else toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const loadAnalysis = async () => {
    try { const { data } = await api.get(`/sessions/${sessionId}/analysis`, { skipAuthRedirect: true }); setAnalysis(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center bg-[var(--bg-base)] text-[var(--txt-muted)] font-mono">Loading session…</div>;

  return (
    <div className="h-screen flex flex-col bg-[var(--bg-base)] text-white overflow-hidden">
      {/* Top bar */}
      <header className="glass border-b border-white/10 flex items-center justify-between px-6 h-16 shrink-0">
        <div className="flex items-center gap-4">
          <button onClick={() => window.location.assign("/")} data-testid="sim-home-btn" className="w-7 h-7 bg-white flex items-center justify-center hover:opacity-80 transition-opacity"><Shield className="w-4 h-4 text-black" /></button>
          <div className="min-w-0">
            <div className="font-display font-bold leading-tight truncate">{data.session.name}</div>
            <div className="text-sm text-[var(--txt-muted)] font-mono truncate">
              {data.session.scenario_type} · {isAdmin ? "FACILITATOR" : data.user.sim_role}
              {!isAdmin && data.user.email && <span className="text-[var(--txt-secondary)]"> · {data.user.email}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-center">
            <div className="overline" style={{ fontSize: "16px" }}>Stage</div>
            <div className="font-mono font-bold">{data.session.current_index >= 0 ? `${data.session.current_index + 1} / ${data.session.total_injects}` : "—"}</div>
          </div>
          {ended ? (
            <div className="text-center">
              <div className="overline" style={{ fontSize: "16px" }}>Time</div>
              <div className="font-mono text-2xl font-bold text-[var(--urgent)]">00:00</div>
            </div>
          ) : current && current.status === "active" && (
            <div className="text-center">
              <div className="overline" style={{ fontSize: "16px" }}>{remaining <= 30 && remaining > 0 ? "Grace" : "Time left"}</div>
              <div className={`font-mono text-2xl font-bold ${remaining <= 30 ? "text-[var(--urgent)] pulse-red" : ""}`}>{paused ? "PAUSE" : fmt(remaining)}</div>
            </div>
          )}
          <span className={`px-2 py-1 text-sm font-mono uppercase border ${data.session.status === "live" ? "border-[var(--success)]/40 text-[var(--success)]" : "border-white/20"}`}>{data.session.status}</span>
          {isAdmin ? (
            <button data-testid="exit-session-btn" onClick={() => nav("/admin?tab=sessions")}
              className="flex items-center gap-2 border border-white/20 px-3 py-1.5 text-base hover:bg-white/10 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Exit to dashboard
            </button>
          ) : (
            <>
              <button data-testid="sim-sessions-btn" onClick={() => nav("/app")}
                className="flex items-center gap-2 border border-white/20 px-3 py-1.5 text-sm hover:bg-white/10 transition-colors">
                <ArrowLeft className="w-4 h-4" /> Sessions
              </button>
              <button data-testid="sim-logout-btn" onClick={logout} className="text-[var(--txt-muted)] hover:text-white"><LogOut className="w-4 h-4" /></button>
            </>
          )}
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* LEFT: Inbox */}
        <aside className="w-80 border-r border-white/10 bg-[var(--bg-surface)] flex flex-col shrink-0">
          <div className="px-4 h-12 flex items-center gap-2 border-b border-white/10"><Inbox className="w-4 h-4" /><span className="overline" style={{ fontSize: "16px" }}>Inbox</span><span className="ml-auto font-mono text-sm text-[var(--txt-muted)]">{data.messages.length}</span></div>
          <div className="flex-1 overflow-auto p-3 space-y-2">
            {data.messages.map((m) => {
              const meta = TAG_META[m.tag] || TAG_META["Internal update"];
              const selected = openMsg?.id === m.id;
              return (
                <button key={m.id} data-testid={`inbox-msg-${m.id}`} onClick={() => setOpenMsg(m)}
                  className={`slide-in elevated p-3 w-full text-left cursor-pointer transition-colors hover:bg-white/5 border-l-2 ${selected ? "border-l-white" : "border-l-transparent"}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <meta.icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                    <span className="text-sm font-mono uppercase" style={{ color: meta.color }}>{m.tag}</span>
                    {m.is_badactor_link && <AlertTriangle className="w-3 h-3 text-[var(--urgent)] ml-auto" />}
                  </div>
                  <div className="text-base font-medium mb-1 truncate">{m.subject}</div>
                  <div className="text-sm text-[var(--txt-secondary)] leading-relaxed line-clamp-2">{m.body}</div>
                </button>
              );
            })}
            {data.messages.length === 0 && <p className="text-sm text-[var(--txt-muted)] p-2">No messages yet.</p>}
          </div>
        </aside>

        {/* CENTER */}
        <main className="flex-1 flex flex-col overflow-hidden">
          <div className={`overflow-auto p-8 ${openMsg ? "flex-1 min-h-0 border-b border-white/10" : "flex-1"}`}>
          {lockedOut ? (
            <div className="max-w-2xl mx-auto text-center py-20">
              <Lock className="w-12 h-12 mx-auto mb-4 text-[var(--urgent)]" />
              <h2 className="font-display font-bold text-2xl mb-2">Device compromised</h2>
              <p className="text-[var(--txt-secondary)]">You clicked a malicious link (simulated device compromise). Access returns at {new Date(data.locked_until).toLocaleTimeString()} (60 seconds).</p>
            </div>
          ) : ended ? (
            isAdmin ? (
              <EndScreen sessionId={sessionId} isAdmin={true} showQ={showQ} setShowQ={setShowQ} />
            ) : feedbackView ? (
              <div className="max-w-2xl mx-auto">
                <button data-testid="back-to-session-btn" onClick={() => setFeedbackView(false)} className="mb-6 flex items-center gap-2 text-base text-[var(--txt-muted)] hover:text-white transition-colors">
                  <ArrowLeft className="w-4 h-4" /> Back to session (read-only)
                </button>
                <EndScreen sessionId={sessionId} isAdmin={false} showQ={showQ} setShowQ={setShowQ} />
              </div>
            ) : (
              <ReadOnlyEnded current={current} data={data} onFeedback={() => setFeedbackView(true)} />
            )
          ) : data.session.status === "briefing" ? (
            <BriefingScreen data={data} isAdmin={isAdmin} />
          ) : !current ? (
            <div className="max-w-2xl mx-auto text-center py-20">
              <Hourglass className="w-12 h-12 mx-auto mb-6 text-[var(--warning)] animate-hourglass" />
              <h2 className="font-display font-bold text-2xl mb-2">Waiting for the facilitator</h2>
              <p className="text-[var(--txt-secondary)]">The simulation has not started yet. Standby.</p>
              <div className="flex items-center justify-center gap-1.5 mt-6">
                <span className="w-2 h-2 bg-[var(--txt-muted)] rounded-full animate-pulse" style={{ animationDelay: "0ms" }} />
                <span className="w-2 h-2 bg-[var(--txt-muted)] rounded-full animate-pulse" style={{ animationDelay: "200ms" }} />
                <span className="w-2 h-2 bg-[var(--txt-muted)] rounded-full animate-pulse" style={{ animationDelay: "400ms" }} />
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto fade-up">
              <div className="overline mb-2" style={{ fontSize: "16px" }}>Inject {current.order + 1} of {data.session.total_injects}</div>
              <h1 className="font-display font-bold text-3xl tracking-tight mb-5">{current.title}</h1>
              <div className="grid-card p-6 mb-6 text-[var(--txt-secondary)] leading-relaxed text-lg"><RichView html={current.body} /></div>

              {!isHtmlEmpty(current.task) && (
                <div data-testid="inject-task" className="grid-card p-5 mb-6 border-l-2 border-l-[var(--warning)]">
                  <div className="overline mb-2 text-[var(--warning)] flex items-center gap-1.5" style={{ fontSize: "16px" }}><ClipboardList className="w-4 h-4" /> Task</div>
                  <RichView html={current.task} className="text-lg leading-relaxed" />
                </div>
              )}

              {data.session.show_analysis && (
                <button data-testid="view-analysis-btn" onClick={loadAnalysis} className="mb-6 flex items-center gap-2 text-base border border-white/20 px-4 py-2 hover:bg-white/10">
                  <Eye className="w-4 h-4" /> View shared analysis
                </button>
              )}

              {!isAdmin && data.user.sim_role !== "Note-taker" && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="overline" style={{ fontSize: "16px" }}>Your response</span>
                    {locked && <span className="text-sm text-[var(--urgent)] flex items-center gap-1"><Lock className="w-3 h-3" /> Locked</span>}
                  </div>
                  <RichText value={response} onChange={setResponse} disabled={locked && !data.my_response} testid="response-input" placeholder="enter answer here" minHeight={140} />
                  <div className="flex gap-3 mt-3">
                    <button data-testid="submit-response-btn" disabled={locked} onClick={() => submit(false)} className="bg-white text-black px-5 py-2.5 text-base font-medium hover:bg-gray-200 disabled:opacity-40 flex items-center gap-2"><Send className="w-4 h-4" /> Submit</button>
                    {locked && <button data-testid="late-response-btn" onClick={() => submit(true)} className="border border-[var(--warning)]/40 text-[var(--warning)] px-5 py-2.5 text-base hover:bg-[var(--warning)]/10 flex items-center gap-2"><Plus className="w-4 h-4" /> Late addition</button>}
                  </div>
                  {data.my_response && data.my_response.text && (
                    <div data-testid="last-answer" className="mt-4 flex items-start gap-2 text-base text-[var(--txt-secondary)] border-t border-white/10 pt-4">
                      <CheckCircle2 className="w-4 h-4 text-[var(--success)] mt-1 shrink-0" />
                      <div>
                        <span className="text-[var(--success)] font-medium">Your last answer:</span>{data.my_response.is_late && <span className="text-[var(--warning)]"> (late)</span>}
                        <RichView html={data.my_response.text} className="mt-1" />
                      </div>
                    </div>
                  )}
                  <MyResponses responses={data.my_responses} />
                </div>
              )}

              {!isAdmin && data.user.sim_role === "Note-taker" && (
                <NoteTaker sessionId={sessionId} injectId={current.id} injectTitle={current.title} stage={current.order + 1} trigger={data.session.trigger} />
              )}

              {analysis && <AnalysisPanel analysis={analysis} onClose={() => setAnalysis(null)} currentId={current.id} />}
            </div>
          )}
          </div>
          {openMsg && (
            <div className="flex-1 min-h-0 overflow-auto p-8 bg-[var(--bg-surface)]/40">
              <EmailView msg={data.messages.find((m) => m.id === openMsg.id) || openMsg} isAdmin={isAdmin} simRole={data.user.sim_role} readOnly={ended} sessionId={sessionId} onLock={load} onClose={() => setOpenMsg(null)} />
            </div>
          )}
        </main>

        {/* RIGHT: Admin controls OR notes view */}
        {isAdmin && session && (
          <AdminControl session={session} sessionId={sessionId} reload={load} loadAnalysis={loadAnalysis} analysis={analysis} setAnalysis={setAnalysis} />
        )}
        {!isAdmin && data.user.sim_role !== "Note-taker" && (
          <TeamNotes sessionId={sessionId} table={data.user.table_number} trigger={data.session.trigger} />
        )}
      </div>
    </div>
  );
}

function BriefingScreen({ data, isAdmin }) {
  const s = data.session;
  return (
    <div className="max-w-3xl mx-auto fade-up" data-testid="briefing-screen">
      <div className="overline mb-2" style={{ fontSize: "16px" }}>Scenario setting · {s.company_name}</div>
      <h1 className="font-display font-bold text-3xl tracking-tight mb-1">{s.scenario_title}</h1>
      <div className="text-sm text-[var(--txt-muted)] font-mono mb-6">{s.scenario_type}</div>
      <div className="grid-card p-6 mb-8 text-[var(--txt-secondary)] leading-relaxed text-lg" data-testid="briefing-trigger-text"><RichView html={s.trigger} /></div>
      <div className="flex items-start gap-3 text-[var(--txt-secondary)]">
        <Play className="w-5 h-5 text-[var(--success)] mt-0.5 shrink-0" />
        <span className="text-lg font-medium" data-testid="briefing-prompt">
          {isAdmin
            ? "When everyone has read the brief, click \u201CStart session\u201D in the facilitator controls."
            : "Ready? Let\u2019s start! Standby \u2014 the facilitator will begin the simulation shortly."}
        </span>
      </div>
    </div>
  );
}

function ReadOnlyEnded({ current, data, onFeedback }) {
  const nav = useNavigate();
  const archiveAt = data.session.archive_at;
  const dl = archiveAt ? Math.max(0, Math.ceil((new Date(archiveAt).getTime() - Date.now()) / 86400000)) : null;
  return (
    <div className="max-w-3xl mx-auto fade-up">
      <div className="elevated p-4 border-l-2 border-l-[var(--warning)] mb-6 flex items-start gap-3">
        <CalendarClock className="w-5 h-5 text-[var(--warning)] shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-base">This session has ended — read only.</p>
          {archiveAt && <p className="text-sm text-[var(--txt-secondary)] font-mono mt-1">Available until {new Date(archiveAt).toLocaleDateString()} · {dl} day{dl === 1 ? "" : "s"} left, then archived.</p>}
        </div>
      </div>
      {current && <>
        <div className="overline mb-2" style={{ fontSize: "16px" }}>Final stage {current.order + 1} of {data.session.total_injects}</div>
        <h1 className="font-display font-bold text-2xl tracking-tight mb-4">{current.title}</h1>
        <div className="grid-card p-6 mb-6 text-[var(--txt-secondary)] leading-relaxed text-lg"><RichView html={current.body} /></div>
        {!isHtmlEmpty(current.task) && (
          <div className="grid-card p-5 mb-6 border-l-2 border-l-[var(--warning)]">
            <div className="overline mb-2 text-[var(--warning)] flex items-center gap-1.5" style={{ fontSize: "16px" }}><ClipboardList className="w-4 h-4" /> Task</div>
            <RichView html={current.task} className="text-lg leading-relaxed" />
          </div>
        )}
      </>}
      {data.my_response && data.my_response.text && (
        <div className="mb-6">
          <div className="overline mb-2" style={{ fontSize: "16px" }}>Your last answer</div>
          <div className="grid-card p-4 text-base text-[var(--txt-secondary)]"><RichView html={data.my_response.text} /></div>
        </div>
      )}
      <button data-testid="open-feedback-btn" onClick={onFeedback} className="bg-white text-black px-6 py-3 text-base font-medium hover:bg-gray-200 flex items-center gap-2">
        <ClipboardList className="w-4 h-4" /> Complete / edit feedback
      </button>
      <button data-testid="ended-view-report-btn" onClick={() => nav(`/report/${data.session.id}`)} className="mt-3 border border-white/25 px-6 py-3 text-base font-medium hover:bg-white/10 flex items-center gap-2">
        <FileText className="w-4 h-4" /> View my debrief report
      </button>
    </div>
  );
}

function EmailView({ msg, isAdmin, simRole, readOnly, sessionId, onLock, onClose }) {
  const meta = TAG_META[msg.tag] || TAG_META["Internal update"];
  const immune = isAdmin || simRole === "Note-taker";
  const clickLink = async () => {
    if (readOnly) { toast("This session has ended — the link is inactive."); onClose(); return; }
    if (immune) {
      toast("Hey there, curiosity almost got you! This link is not activated for note takers and admin.");
      onClose();
      return;
    }
    await api.post(`/sessions/${sessionId}/messages/${msg.id}/click-link`);
    toast.error("Compromised! Locked out for 60 seconds.");
    onClose();
    onLock();
  };
  return (
    <div data-testid="email-view" className="max-w-3xl mx-auto fade-up">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <meta.icon className="w-5 h-5" style={{ color: meta.color }} />
          <span className="text-sm font-mono uppercase" style={{ color: meta.color }}>{msg.tag}</span>
          <span className="text-sm font-mono text-[var(--txt-muted)]">· {new Date(msg.created_at).toLocaleTimeString()}</span>
        </div>
        <button data-testid="email-close-btn" onClick={onClose} className="text-[var(--txt-muted)] hover:text-white flex items-center gap-1 text-sm">
          <XCircle className="w-4 h-4" /> Close
        </button>
      </div>
      <h2 className="font-display font-bold text-2xl tracking-tight mb-4">{msg.subject}</h2>
      <div className="grid-card p-6 text-[var(--txt-secondary)] leading-relaxed whitespace-pre-wrap text-lg">{msg.body}</div>
      {msg.is_badactor_link && (
        <button data-testid={`badactor-link-${msg.id}`} onClick={clickLink}
          className="mt-5 inline-flex items-center gap-2 border border-[var(--urgent)]/40 text-[var(--urgent)] px-4 py-2.5 text-base hover:bg-[var(--urgent)]/10 transition-colors">
          <AlertTriangle className="w-4 h-4" /> Click here to verify your account
        </button>
      )}
    </div>
  );
}

function MyResponses({ responses }) {
  const [open, setOpen] = useState(true);
  if (!responses || !responses.length) return null;
  const byStage = {};
  responses.forEach((r) => { (byStage[r.stage] = byStage[r.stage] || []).push(r); });
  const stages = Object.keys(byStage).map(Number).sort((a, b) => b - a);
  return (
    <div data-testid="my-responses" className="mt-8 border-t border-white/10 pt-5">
      <button data-testid="toggle-my-responses" onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 overline mb-3 text-[var(--txt-secondary)] hover:text-white transition-colors" style={{ fontSize: "16px" }}>
        <History className="w-4 h-4" /> Your previous responses ({responses.length})
      </button>
      {open && (
        <div className="space-y-5">
          {stages.map((st) => (
            <div key={st} data-testid={`my-responses-stage-${st}`}>
              <div className="overline mb-2" style={{ fontSize: "14px" }}>Stage {st}{byStage[st][0].inject_title ? ` · ${byStage[st][0].inject_title}` : ""}</div>
              <div className="space-y-2">
                {byStage[st].map((r) => (
                  <div key={r.id} className="elevated p-3 text-base slide-in">
                    <div className="text-sm text-[var(--txt-muted)] font-mono mb-1">{new Date(r.created_at).toLocaleTimeString()}{r.is_late && <span className="text-[var(--warning)]"> · late</span>}</div>
                    <RichView html={r.text} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function RollingNotes({ notes, emptyText, trigger }) {
  const byStage = {};
  notes.forEach((n) => { (byStage[n.stage] = byStage[n.stage] || []).push(n); });
  const stages = Object.keys(byStage).map(Number).sort((a, b) => b - a);
  const hasTrigger = !isHtmlEmpty(trigger);
  if (!notes.length && !hasTrigger) return <p className="text-sm text-[var(--txt-muted)] p-1">{emptyText}</p>;
  return (
    <div className="space-y-5">
      {hasTrigger && (
        <div data-testid="trigger-note" className="elevated p-3 border-l-2 border-l-[var(--warning)]">
          <div className="overline mb-1.5 flex items-center gap-1.5 text-[var(--warning)]" style={{ fontSize: "14px" }}><Lock className="w-3 h-3" /> The trigger · scenario setting</div>
          <RichView html={trigger} className="text-base text-[var(--txt-secondary)]" />
        </div>
      )}
      {stages.map((st) => (
        <div key={st} data-testid={`notes-stage-${st}`}>
          <div className="overline mb-2" style={{ fontSize: "14px" }}>Stage {st} notes</div>
          <div className="space-y-2">
            {byStage[st].map((n) => (
              <div key={n.id} className="elevated p-3 text-base slide-in">
                <div className="text-sm text-[var(--txt-muted)] font-mono mb-1">{new Date(n.created_at).toLocaleTimeString()}</div>
                {n.text}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function NoteTaker({ sessionId, injectId, injectTitle, stage, trigger }) {
  const [text, setText] = useState("");
  const [notes, setNotes] = useState([]);
  const load = useCallback(() => api.get(`/sessions/${sessionId}/notes`).then((r) => setNotes(r.data)), [sessionId]);
  useEffect(() => { load(); const id = setInterval(load, 4000); return () => clearInterval(id); }, [load]);
  const add = async () => { if (!text.trim()) return; await api.post(`/sessions/${sessionId}/notes`, { text, inject_id: injectId }); setText(""); load(); };
  return (
    <div>
      <div className="flex items-center gap-2 mb-2"><NotebookPen className="w-4 h-4" /><span className="overline" style={{ fontSize: "16px" }}>Capturing — Stage {stage}: "{injectTitle}"</span></div>
      <textarea data-testid="note-input" value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="enter answer here" className="w-full grid-card p-4 outline-none focus:border-white text-base" />
      <button data-testid="add-note-btn" onClick={add} className="mt-3 bg-white text-black px-5 py-2.5 text-base font-medium hover:bg-gray-200 flex items-center gap-2"><Plus className="w-4 h-4" /> Add note</button>
      <div className="mt-6">
        <div className="overline mb-3" style={{ fontSize: "16px" }}>Running notes (newest first)</div>
        <RollingNotes notes={notes} trigger={trigger} emptyText="No notes captured yet." />
      </div>
    </div>
  );
}

function TeamNotes({ sessionId, table, trigger }) {
  const [notes, setNotes] = useState([]);
  const load = useCallback(() => api.get(`/sessions/${sessionId}/notes`).then((r) => setNotes(r.data)), [sessionId]);
  useEffect(() => { load(); const id = setInterval(load, 4000); return () => clearInterval(id); }, [load]);
  return (
    <aside className="w-72 border-l border-white/10 bg-[var(--bg-surface)] flex flex-col shrink-0">
      <div className="px-4 h-12 flex items-center gap-2 border-b border-white/10"><NotebookPen className="w-4 h-4" /><span className="overline" style={{ fontSize: "16px" }}>Team {table} notes</span></div>
      <div className="flex-1 overflow-auto p-3">
        <RollingNotes notes={notes} trigger={trigger} emptyText="No notes yet (view-only)." />
      </div>
    </aside>
  );
}

function fmtHM(totalSec) {
  if (totalSec < 0) totalSec = 0;
  const h = Math.floor(totalSec / 3600), m = Math.floor((totalSec % 3600) / 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function DayClock({ session }) {
  const [now, setNow] = useState(Date.now());
  const alerted = useRef(new Set());
  const storeKey = `tg_breaks_done_${session.id}`;
  const [done, setDone] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(storeKey) || "[]")); } catch { return new Set(); }
  });
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);

  const start = session.started_at ? new Date(session.started_at).getTime() : null;
  const planned = (session.duration_minutes || 0) * 60;
  const elapsed = start ? Math.floor((now - start) / 1000) : 0;
  const pct = planned ? Math.min(100, (elapsed / planned) * 100) : 0;

  const targetFor = (b) => {
    if (!start) return null;
    if (b.mode === "time" && b.at_time) {
      const [h, m] = b.at_time.split(":").map(Number);
      const d = new Date(start); d.setHours(h, m, 0, 0); return d.getTime();
    }
    return start + (b.offset_minutes || 0) * 60000;
  };
  const breaks = (session.breaks || []).map((b, i) => ({ ...b, _i: i, _id: b.id || `i${i}`, target: targetFor(b) }))
    .filter((b) => b.target != null).sort((a, b) => a.target - b.target);

  const markDone = (id) => {
    const nd = new Set(done); nd.add(id); setDone(nd);
    localStorage.setItem(storeKey, JSON.stringify([...nd]));
  };

  useEffect(() => {
    breaks.forEach((b) => {
      if (b.target && now >= b.target && !done.has(b._id) && !alerted.current.has(b._id)) {
        alerted.current.add(b._id);
        toast.warning(`Break due: ${b.label || "Break"}${b.duration_minutes ? ` (${b.duration_minutes} min)` : ""}`, { duration: 10000 });
      }
    });
  });

  if (!start) return null;

  return (
    <div className="elevated p-3" data-testid="day-clock">
      <div className="flex items-center justify-between mb-2">
        <span className="overline flex items-center gap-1.5" style={{ fontSize: "16px" }}><Clock className="w-4 h-4" /> Day clock</span>
        <span className="font-mono text-sm" data-testid="day-clock-elapsed">{fmtHM(elapsed)}{planned ? ` / ${fmtHM(planned)}` : ""}</span>
      </div>
      {planned > 0 && <div className="h-1.5 bg-white/10 rounded overflow-hidden mb-3"><div className="h-full bg-[var(--success)] transition-[width] duration-1000" style={{ width: `${pct}%` }} /></div>}
      {breaks.length === 0 ? (
        <p className="text-sm text-[var(--txt-muted)]">No breaks scheduled.</p>
      ) : (
        <div className="space-y-1.5">
          {breaks.map((b) => {
            const due = now >= b.target && !done.has(b._id);
            const dismissed = done.has(b._id);
            return (
              <div key={b._id} data-testid={`day-break-${b._i}`}
                className={`flex items-center gap-2 text-sm px-2 py-1.5 border-l-2 ${due ? "border-l-[var(--urgent)] bg-[var(--urgent)]/10" : dismissed ? "border-l-transparent opacity-50" : "border-l-white/20"}`}>
                <span className="font-mono text-[var(--txt-muted)] w-14 shrink-0">{new Date(b.target).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                <span className="flex-1 truncate">{b.label || "Break"}{b.duration_minutes ? ` · ${b.duration_minutes}m` : ""}</span>
                {due ? (
                  <button data-testid={`break-done-${b._i}`} onClick={() => markDone(b._id)} className="text-xs bg-white text-black px-2 py-1 font-medium hover:bg-gray-200">Mark taken</button>
                ) : dismissed ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-[var(--success)] shrink-0" />
                ) : (
                  <span className="text-xs text-[var(--txt-muted)] shrink-0">upcoming</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AdminControl({ session, sessionId, reload, loadAnalysis, analysis, setAnalysis }) {
  const [msg, setMsg] = useState({ tag: "Media", subject: "", body: "", is_badactor_link: false });
  const [ext, setExt] = useState(60);
  const cur = session.injects[session.current_index];

  const act = async (path, body) => { await api.post(`/sessions/${sessionId}/${path}`, body || {}); reload(); };
  const sendMsg = async () => {
    if (!msg.subject) return toast.error("Subject required");
    await api.post(`/sessions/${sessionId}/messages`, msg);
    toast.success("Message broadcast"); setMsg({ ...msg, subject: "", body: "" });
  };

  return (
    <aside className="w-96 border-l border-white/10 bg-[var(--bg-surface)] flex flex-col shrink-0 overflow-auto">
      <div className="px-4 h-12 flex items-center gap-2 border-b border-white/10 sticky top-0 bg-[var(--bg-surface)] z-10"><Settings className="w-4 h-4" /><span className="overline" style={{ fontSize: "16px" }}>Facilitator controls</span></div>
      <div className="p-4 space-y-5">
        <DayClock session={session} />
        <div className="grid grid-cols-2 gap-2">
          {session.status === "draft" && <CtrlBtn testid="launch-btn" onClick={() => act("launch")} icon={Play} label="Launch" primary full />}
          {session.status === "briefing" && <CtrlBtn testid="start-btn" onClick={() => act("begin")} icon={Play} label="Start session" primary full />}
          {(session.status === "live" || session.status === "paused") && <>
            <CtrlBtn testid="pause-btn" onClick={() => act("pause")} icon={session.status === "paused" ? Play : Pause} label={session.status === "paused" ? "Resume" : "Pause"} />
            <CtrlBtn testid="next-btn" onClick={() => act("next")} icon={SkipForward} label="Next / Skip" />
          </>}
          {session.status === "live" && <CtrlBtn testid="end-btn" onClick={() => { if (window.confirm("End session?")) act("end"); }} icon={XCircle} label="End session" />}
        </div>

        {cur && (session.status === "live" || session.status === "paused") && (
          <div className="elevated p-3">
            <div className="overline mb-2" style={{ fontSize: "16px" }}>Adjust timer · {cur.title}</div>
            <div className="flex items-center gap-2">
              <input data-testid="extend-seconds-input" type="number" value={ext} onChange={(e) => setExt(parseInt(e.target.value) || 0)} className="w-20 elevated px-2 py-1.5 text-base outline-none" />
              <button data-testid="extend-btn" onClick={() => act("extend", { inject_id: cur.id, seconds: ext })} className="flex items-center gap-1 border border-white/20 px-3 py-1.5 text-base hover:bg-white/10"><Plus className="w-3 h-3" /> Add</button>
              <button data-testid="reduce-btn" onClick={() => act("extend", { inject_id: cur.id, seconds: -ext })} className="flex items-center gap-1 border border-white/20 px-3 py-1.5 text-base hover:bg-white/10"><Minus className="w-3 h-3" /> Cut</button>
            </div>
          </div>
        )}

        {/* Send message */}
        <div className="elevated p-3 space-y-3">
          <div className="overline" style={{ fontSize: "16px" }}>Broadcast inbox message</div>
          <select data-testid="msg-tag-select" value={msg.tag} onChange={(e) => setMsg({ ...msg, tag: e.target.value, is_badactor_link: e.target.value === "Bad actor" ? msg.is_badactor_link : false })} className="w-full elevated px-2 py-2 text-base outline-none">
            {Object.keys(TAG_META).map((t) => <option key={t} className="bg-[var(--bg-elevated)]">{t}</option>)}
          </select>
          <input data-testid="msg-subject-input" value={msg.subject} onChange={(e) => setMsg({ ...msg, subject: e.target.value })} placeholder="Subject" className="w-full elevated px-2 py-2 text-base outline-none" />
          <textarea data-testid="msg-body-input" value={msg.body} onChange={(e) => setMsg({ ...msg, body: e.target.value })} placeholder="Message body" rows={3} className="w-full elevated px-2 py-2 text-base outline-none" />
          {msg.tag === "Bad actor" && (
            <label className="flex items-center gap-2 text-sm text-[var(--txt-secondary)]">
              <input data-testid="msg-badactor-checkbox" type="checkbox" checked={msg.is_badactor_link} onChange={(e) => setMsg({ ...msg, is_badactor_link: e.target.checked })} />
              <AlertTriangle className="w-3 h-3 text-[var(--urgent)]" /> Include malicious link (60-second lockout on click)
            </label>
          )}
          <button data-testid="send-msg-btn" onClick={sendMsg} className="w-full bg-white text-black py-2 text-base font-medium hover:bg-gray-200 flex items-center justify-center gap-2"><Send className="w-4 h-4" /> Broadcast</button>
        </div>

        {/* Analysis */}
        <div className="elevated p-3 space-y-3">
          <div className="overline" style={{ fontSize: "16px" }}>Live intelligence</div>
          <button data-testid="admin-analysis-btn" onClick={loadAnalysis} className="w-full border border-white/20 py-2 text-base hover:bg-white/10 flex items-center justify-center gap-2"><BarChart3 className="w-4 h-4" /> Run analysis</button>
          <button data-testid="toggle-analysis-btn" onClick={() => act("toggle-analysis")} className={`w-full py-2 text-base flex items-center justify-center gap-2 ${session.show_analysis ? "bg-[var(--success)]/20 text-[var(--success)] border border-[var(--success)]/40" : "border border-white/20 hover:bg-white/10"}`}>
            <Eye className="w-4 h-4" /> {session.show_analysis ? "Hiding from participants" : "Show to participants"}
          </button>
        </div>
      </div>

      {analysis && (
        <div className="border-t border-white/10 p-4">
          <AnalysisPanel analysis={analysis} onClose={() => setAnalysis(null)} inline />
        </div>
      )}
    </aside>
  );
}

function CtrlBtn({ onClick, icon: Icon, label, primary, full, testid }) {
  return <button data-testid={testid} onClick={onClick} className={`${full ? "col-span-2" : ""} flex items-center justify-center gap-2 py-2.5 text-base font-medium transition-colors ${primary ? "bg-white text-black hover:bg-gray-200" : "border border-white/20 hover:bg-white/10"}`}><Icon className="w-4 h-4" /> {label}</button>;
}

function AnalysisPanel({ analysis, onClose, inline, currentId }) {
  const list = currentId ? analysis.filter((a) => a.inject_id === currentId) : analysis;
  return (
    <div data-testid="analysis-panel" className={inline ? "" : "grid-card p-6 mt-6"}>
      <div className="flex items-center justify-between mb-4"><span className="overline" style={{ fontSize: "16px" }}>Matches · Clashes · Missed tasks</span>{!inline && <button onClick={onClose} className="text-[var(--txt-muted)] hover:text-white"><XCircle className="w-4 h-4" /></button>}</div>
      <div className="space-y-4">
        {list.map((a) => (
          <div key={a.inject_id} className="border-b border-white/10 pb-3 last:border-0">
            <div className="font-display font-bold text-base mb-2">{a.order + 1}. {a.title} <span className="text-[var(--txt-muted)] font-mono font-normal">({a.response_count} responses)</span></div>
            {a.matches.map((m, i) => <div key={`m${i}`} className="flex items-start gap-2 text-sm mb-1"><CheckCircle2 className="w-3.5 h-3.5 text-[var(--success)] mt-0.5 shrink-0" /><span><b>Match:</b> {m.phrase} <span className="text-[var(--txt-muted)]">({m.by.join(", ")})</span></span></div>)}
            {a.clashes.map((c, i) => <div key={`c${i}`} className="flex items-start gap-2 text-sm mb-1"><AlertTriangle className="w-3.5 h-3.5 text-[var(--warning)] mt-0.5 shrink-0" /><span><b>Clash:</b> {c.map((x) => `${x.term} (${x.by.join("/")})`).join(" vs ")}</span></div>)}
            {a.missed.map((m, i) => <div key={`x${i}`} className="flex items-start gap-2 text-sm mb-1"><XCircle className="w-3.5 h-3.5 text-[var(--urgent)] mt-0.5 shrink-0" /><span><b>Missed:</b> {m}</span></div>)}
            {a.matches.length === 0 && a.clashes.length === 0 && a.missed.length === 0 && <span className="text-sm text-[var(--txt-muted)]">No signals yet.</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function EndScreen({ sessionId, isAdmin, showQ, setShowQ }) {
  const nav = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  useEffect(() => {
    api.get(`/sessions/${sessionId}/questionnaire`).then((r) => {
      setQuestions(r.data.questions);
      if (r.data.answers) { setAnswers(r.data.answers.answers); setSubmitted(true); }
    });
  }, [sessionId]);
  const submit = async () => { await api.post(`/sessions/${sessionId}/questionnaire`, { answers }); toast.success("Response submitted"); setSubmitted(true); };

  if (isAdmin) return (
    <div className="max-w-xl mx-auto text-center py-20">
      <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-[var(--success)]" />
      <h2 className="font-display font-bold text-2xl mb-2">Session ended</h2>
      <p className="text-[var(--txt-secondary)]">Download the debrief PDF and raw data exports from the Sessions list. Data archives in 30 days.</p>
    </div>
  );

  if (submitted) return (
    <div data-testid="thank-you-screen" className="max-w-xl mx-auto text-center py-32 fade-up">
      <p className="font-hand text-[var(--txt-primary)] leading-snug" style={{ fontSize: "22px" }}>
        Thank you for your time today! You can now exit the session
      </p>
      <button data-testid="view-my-report-btn" onClick={() => nav(`/report/${sessionId}`)} className="mt-10 border border-white/25 px-6 py-3 text-base font-medium hover:bg-white/10 transition-colors inline-flex items-center gap-2">
        <FileText className="w-4 h-4" /> View my debrief report
      </button>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto py-10 fade-up">
      <div className="overline mb-2" style={{ fontSize: "16px" }}>Session feedback</div>
      <h2 className="font-display font-bold text-3xl mb-2">Before you go.</h2>
      <p className="text-[var(--txt-secondary)] mb-8">Your answers are included in your personal debrief report. You can review and edit before final submit.</p>
      <div className="space-y-6">
        {questions.map((q) => (
          <div key={q}>
            <label className="block font-medium mb-2">{q}</label>
            <textarea data-testid={`q-input-${questions.indexOf(q)}`} value={answers[q] || ""} onChange={(e) => setAnswers({ ...answers, [q]: e.target.value })} rows={3} placeholder="enter answer here" className="w-full grid-card p-4 outline-none focus:border-white text-base" />
          </div>
        ))}
      </div>
      <button data-testid="submit-questionnaire-btn" onClick={submit} className="mt-6 bg-white text-black px-6 py-3 font-medium hover:bg-gray-200 flex items-center gap-2"><Send className="w-4 h-4" /> Submit answers</button>
    </div>
  );
}
